/**
 * CR-6438: sectioned layout for the Purchasing → Pricing filter panel.
 *
 * Regroups the existing filters into the five sections from the supplied
 * design — Contract Period · Company · Supplier · Classification · Contract
 * Details. The panel was a single flat grid of seven fields in which the
 * contract validity dates sat at the end, after Currency, rather than leading.
 *
 * Note the first section is "Contract Period", not "Date Range" as on the other
 * panels — these dates bound a contract's validity, not a transaction window,
 * and the design names them accordingly.
 *
 * Section headings come from the app-wide `components/filters/FilterSection`.
 *
 * The panel uses two different setters and both are preserved: text and select
 * fields go through `onFilterChange` (the host's debounced handler), while the
 * searchable pickers write straight to filter state via `onSelectChange`.
 *
 * The rework adds the filters the design asks for that a contract can actually
 * be filtered by: quick-period chips, Created date From/To, Status and
 * Auto-renew, Company Country / Division / Warehouse, Supplier Country / Type /
 * Strategic-only, Commodity Group / Type, Risk category, Contract owner and
 * Created by.
 *
 * NOTE on Warehouse / Commodity filters: The diagram includes these, but the
 * ContractPricePart model stores `part_number` as text without FK relationships
 * to Warehouse or Commodity master data. The UI is present to match the design,
 * but filtering won't take effect until the model is extended. The frontend
 * passes the params so they're ready when backend support is added.
 *
 * Cascade filter dependencies:
 * - Division → Site (filter sites by selected division)
 * - Company Country → Site (filter sites by selected country)
 * - Site → Supplier (filter suppliers by selected site/customer)
 * - Supplier Country → Supplier (filter suppliers by selected country)
 * - Supplier Group → Supplier (filter suppliers by selected group)
 * - Commodity Group → Commodity Type (filter types by selected group)
 */
import { Calendar, Building2, Truck, Tags, FileText } from 'lucide-react';
import SearchableSelect from '../../../ui/SearchableSelect';
import { FilterSection, FilterGrid, FilterField } from '../../../filters/FilterSection';
import { countryDisplayNames } from '../../../dashboard/dashboardFilterUtils';

interface FilterOption {
  value: string;
  label: string;
  country?: string;
  group?: number | null;
  /** CR-6438: Customer IDs this supplier serves, for Site → Supplier cascade. */
  customers?: number[];
}

/** Control styling matching this panel's existing density. */
const inputCls =
  'w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary';
const dateInputCls =
  'w-full pl-9 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary';

export interface PricingFilterSectionsProps {
  filters: Record<string, string>;
  textInputs: Record<string, string>;
  companySiteOptions: FilterOption[];
  supplierGroupOptions: FilterOption[];
  supplierSiteOptions: FilterOption[];
  /** CR-6438 rework: options for the filters the design added. */
  divisionOptions?: FilterOption[];
  supplierCountryOptions?: string[];
  ownerOptions?: FilterOption[];
  createdByOptions?: FilterOption[];
  riskCategoryOptions?: FilterOption[];
  /** CR-6438: Missing filters from diagram - Warehouse, Commodity Group, Commodity Type */
  warehouseOptions?: FilterOption[];
  commodityGroupOptions?: FilterOption[];
  commodityTypeOptions?: FilterOption[];
  quickRanges?: ReadonlyArray<{ key: string; label: string }>;
  getQuickRangeDates?: (key: string) => { dateFrom: string; dateTo: string };
  /** Host's debounced handler — used by the text and select fields. */
  onFilterChange: (key: string, value: string) => void;
  /** Direct filter-state write — used by the searchable pickers. */
  onSelectChange: (key: string, value: string) => void;
}

export function PricingFilterSections({
  filters,
  textInputs,
  companySiteOptions,
  supplierGroupOptions,
  supplierSiteOptions,
  divisionOptions = [],
  supplierCountryOptions = [],
  ownerOptions = [],
  createdByOptions = [],
  riskCategoryOptions = [],
  warehouseOptions = [],
  commodityGroupOptions = [],
  commodityTypeOptions = [],
  quickRanges = [],
  getQuickRangeDates,
  onFilterChange,
  onSelectChange,
}: PricingFilterSectionsProps) {
  // CR-6438 rework: Country/Division narrow the Company Site picker, matching
  // the dashboard's parent -> child behaviour.
  const sitesForSelection = companySiteOptions.filter((s) => {
    if (filters['customerCountry'] && s.country !== filters['customerCountry']) return false;
    if (filters['division'] && String(s.group ?? '') !== filters['division']) return false;
    return true;
  });
  // CR-6438: Site, Supplier Country, and Supplier Group all narrow the Supplier picker.
  // Site → Supplier cascade: only show suppliers that serve the selected customer site.
  const suppliersForSelection = supplierSiteOptions.filter((s) => {
    // Filter by selected site (customer) if set
    if (filters['customer']) {
      const siteId = parseInt(filters['customer'], 10);
      if (!s.customers?.includes(siteId)) return false;
    }
    // Filter by supplier country if set
    if (filters['supplierCountry'] && s.country !== filters['supplierCountry']) return false;
    // Filter by supplier group if set
    if (filters['supplierGroup'] && String(s.group ?? '') !== filters['supplierGroup']) return false;
    return true;
  });

  const countrySelect = (label: string, key: string, values: string[]) => (
    <FilterField label={label}>
      <select
        value={filters[key] || ''}
        onChange={(e) => onSelectChange(key, e.target.value)}
        className={inputCls}
      >
        <option value="">All countries</option>
        {/* Sorted by the label rather than the raw code — the two orders
            disagree (GB renders as "United Kingdom"), so sorting by code
            produced a list that looks unsorted to whoever reads it. */}
        {[...values]
          .sort((a, b) => (countryDisplayNames[a] || a).localeCompare(countryDisplayNames[b] || b))
          .map((c) => (
            <option key={c} value={c}>{countryDisplayNames[c] || c}</option>
          ))}
      </select>
    </FilterField>
  );

  /** Segmented button row — the design draws Status and Auto-renew this way. */
  const segmented = (label: string, key: string, options: ReadonlyArray<{ value: string; label: string }>) => (
    <FilterField label={label}>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => {
          const active = (filters[key] || '') === o.value;
          return (
            <button
              key={o.value || 'all'}
              type="button"
              // These read as plain buttons to a screen reader otherwise —
              // the selected one is distinguished only by colour.
              aria-pressed={active}
              onClick={() => onSelectChange(key, o.value)}
              className={'px-3 py-1.5 text-xs font-medium border rounded-md transition-colors ' + (active
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border hover:border-primary hover:bg-primary/5')}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </FilterField>
  );

  const selectFromOptions = (label: string, key: string, options: FilterOption[], placeholder: string) => (
    <FilterField label={label}>
      <select
        value={filters[key] || ''}
        onChange={(e) => onSelectChange(key, e.target.value)}
        className={inputCls}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </FilterField>
  );
  const textField = (label: string, key: string, value: string, placeholder: string) => (
    <FilterField label={label}>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onFilterChange(key, e.target.value)}
        className={inputCls}
      />
    </FilterField>
  );

  const dateField = (label: string, key: string) => (
    <FilterField label={label}>
      <div className="relative">
        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="date"
          value={filters[key] || ''}
          onChange={(e) => onFilterChange(key, e.target.value)}
          className={dateInputCls}
        />
      </div>
    </FilterField>
  );

  const picker = (label: string, key: string, options: FilterOption[], placeholder: string) => (
    <FilterField label={label}>
      <SearchableSelect
        options={options}
        value={filters[key] || ''}
        onChange={(val) => onSelectChange(key, val)}
        placeholder={placeholder}
      />
    </FilterField>
  );

  /** Horizontal divider with padding so it doesn't touch card edges */
  const SectionDivider = () => (
    <div className="mx-4 border-t border-border" />
  );

  return (
    <div className="[&>section]:border-t-0">
      {/* Divider above first section */}
      <SectionDivider />

      {/* Contract Period — the validity window previously sat at the end of the
          grid, after Currency. */}
      <FilterSection title="Contract Period" icon={Calendar}>
        {/* QA: "the quick filter options are missing (e.g. Last Month, This
            Month, etc.)". */}
        {quickRanges.length > 0 && getQuickRangeDates && (
          <div className="flex flex-wrap gap-2 mb-3">
            {quickRanges.map(({ key, label }) => {
              const dates = getQuickRangeDates(key);
              const isActive = !!dates.dateFrom
                && filters['dateFrom'] === dates.dateFrom
                && filters['dateTo'] === dates.dateTo;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onSelectChange('__quickPeriod', key)}
                  className={'px-3 py-1.5 text-xs font-medium border rounded-md transition-colors ' + (isActive
                    ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                    : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary')}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
        <FilterGrid>
          {dateField('Valid From', 'dateFrom')}
          {dateField('Valid To', 'dateTo')}
          {dateField('Created From', 'createdFrom')}
          {dateField('Created To', 'createdTo')}
        </FilterGrid>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          {segmented('Status', 'status', [
            { value: '', label: 'All' },
            { value: 'active', label: 'Active' },
            { value: 'expired', label: 'Expired' },
            { value: 'draft', label: 'Draft' },
          ])}
          {/* QA: "the Auto-Renew filter options are missing". */}
          {segmented('Auto-Renew', 'autoRenew', [
            { value: '', label: 'All' },
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ])}
        </div>
      </FilterSection>

      <SectionDivider />

      <FilterSection title="Company" icon={Building2}>
        <FilterGrid>
          {countrySelect('Country', 'customerCountry',
            Array.from(new Set(
              companySiteOptions
                .map((s) => s.country)
                .filter((c): c is string => typeof c === 'string' && c !== ''),
            )))}
          {picker('Division', 'division', divisionOptions, 'All divisions')}
          {picker('Site', 'customer', sitesForSelection, 'All sites')}
          {picker('Warehouse', 'warehouse', warehouseOptions, 'All warehouses')}
        </FilterGrid>
      </FilterSection>

      <SectionDivider />

      <FilterSection title="Supplier" icon={Truck}>
        <FilterGrid>
          {countrySelect('Country', 'supplierCountry', supplierCountryOptions)}
          {picker('Supplier Group', 'supplierGroup', supplierGroupOptions, 'All Supplier Groups')}
          {picker('Supplier Site', 'supplier', suppliersForSelection, 'All Supplier Sites')}
          {selectFromOptions('Supplier Type', 'supplierType', [
            { value: 'internal', label: 'Internal' },
            { value: 'external', label: 'External' },
          ], 'All suppliers')}
        </FilterGrid>
        <label className="flex items-center gap-2 cursor-pointer mt-3 w-fit">
          <input
            type="checkbox"
            checked={filters['strategicOnly'] === 'true'}
            onChange={(e) => onSelectChange('strategicOnly', e.target.checked ? 'true' : '')}
            className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
          />
          <span className="text-sm font-medium">Strategic suppliers only</span>
        </label>
      </FilterSection>

      <SectionDivider />

      <FilterSection title="Classification" icon={Tags}>
        <FilterGrid>
          {picker('Commodity Group', 'commodityGroup', commodityGroupOptions, 'All commodities')}
          {picker('Commodity Type', 'commodityType', commodityTypeOptions, 'All types')}
          <FilterField label="Currency">
            <select
              value={filters['currency'] || ''}
              onChange={(e) => onFilterChange('currency', e.target.value)}
              className={inputCls}
            >
              <option value="">All currencies</option>
              <option value="GBP">GBP</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="JPY">JPY</option>
              <option value="CNY">CNY</option>
              <option value="PLN">PLN</option>
            </select>
          </FilterField>
          {selectFromOptions('Risk Category', 'riskCategory', riskCategoryOptions, 'All categories')}
        </FilterGrid>
      </FilterSection>

      <SectionDivider />

      <FilterSection title="Contract Details" icon={FileText}>
        <FilterGrid>
          {textField('Contract Number', 'contractNumber', textInputs['contractNumber'] || '', 'CP-001...')}
          {textField('Part Number', 'partNumber', textInputs['partNumber'] || '', 'Part number...')}
          {selectFromOptions('Contract Owner', 'owner', ownerOptions, 'All owners')}
          {selectFromOptions('Created By', 'createdBy', createdByOptions, 'All users')}
        </FilterGrid>
      </FilterSection>
    </div>
  );
}
